<template>
  <StandardCard
    :title-action="viewHistoryTitleAction"
    card-test-id="license-assignment-section"
    title="Assigned to"
    title-action-cols="4"
    border
    tile
  >
    <div class="d-flex flex-column ga-4">
      <div class="d-flex align-center ga-2">
        <v-icon
          :icon="assigneeIcon"
          class="material-icons-outlined"
          color="black"
        />

        <span class="fs-16">{{ currentAssignee.name || blankValue }}</span>
      </div>

      <div
        v-if="showAssigneeEmail"
        class="d-flex align-center ga-2"
      >
        <v-icon
          class="material-icons-outlined"
          color="black"
          icon="mail"
        />

        <span class="fs-16">{{ currentAssignee.email || blankValue }}</span>
      </div>
    </div>

    <template
      v-if="isSpecialist"
      #actions
    >
      <v-card-actions class="d-flex">
        <StandardButton
          @click="handleAssignClick"
          :disabled-text="assignDisabledText"
          :disabled-text-translate="false"
          :prepend-icon="assignAction.prependIcon"
          :role="assignAction.role"
          :text="assignAction.title"
          full-width
        />
      </v-card-actions>
    </template>
  </StandardCard>

  <LicenseAssignDialog
    v-if="isSpecialist && assignActionAvailable"
    v-model:member-items="memberItems"
    @save="saveAssignment"
    @search-members="searchMembers"
    ref="assignDialog"
    :allow-team="allowTeam"
    :member-search-loading="memberSearchLoading"
    :processing="processing"
    :team-items="teamItems"
  />
</template>

<script setup>
import LicenseAssignDialog from '@/shared/components/licenses/LicenseAssignDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';

const eventBus = useEventBus();
const { api: Api, scopedApi } = useApi();
const { isSpecialist } = useRole();

const props = defineProps({
  blankValue: {
    type: String,
    default: '--',
  },
  license: {
    type: Object,
    default: null,
  },
  workflowInstance: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['save']);

const assignDialog = ref(null);
const processing = ref(false);
const memberItems = ref([]);
const memberQueryCount = ref(0);
const memberSearchLoading = ref(false);
const teamItems = ref([]);

const allowTeam = computed(() => Boolean(props.workflowInstance));

const currentAssignee = computed(() => {
  if (props.workflowInstance) {
    const assignee = props.workflowInstance.assignee;
    return {
      id: assignee?.id ?? null,
      type: assignee?.type ?? null,
      name: assignee?.name ?? null,
      email: assignee?.email ?? null,
    };
  }

  return {
    id: props.license?.reviewer_id ?? null,
    type: 'Member',
    name: props.license?.reviewer_name ?? null,
    email: props.license?.reviewer_email ?? null,
  };
});

const assigneeIcon = computed(() => (currentAssignee.value.type === 'Team' ? 'group' : 'person'));

const showAssigneeEmail = computed(() => currentAssignee.value.type !== 'Team');

const assignActionAvailable = computed(() => {
  if (props.workflowInstance) {
    return Boolean(props.workflowInstance.id && scopedApi?.workflowInstance?.update);
  }
  return Boolean(props.license?.id && scopedApi?.license?.update);
});

const assignAction = computed(() => ({
  title: currentAssignee.value.id ? 'Reassign' : 'Assign',
  prependIcon: 'person_add',
  role: 'secondary',
}));

const assignDisabledText = computed(() => {
  if (assignActionAvailable.value) return null;
  return 'Assign functionality not available yet';
});

const viewHistoryTitleAction = computed(() => {
  if (!isSpecialist) return null;

  return {
    customClasses: 'c-primary flex-shrink-0',
    disabledText: 'View history not available yet',
    disabledTextTranslate: false,
    text: 'View history',
    textTranslate: false,
    variant: 'text',
  };
});

function handleAssignClick() {
  if (assignActionAvailable.value) {
    void openAssignDialog();
  }
}

async function openAssignDialog() {
  if (!props.license && !props.workflowInstance) return;

  await Promise.all([searchMembers(''), loadTeamsIfNeeded()]);

  await assignDialog.value?.open({
    assignee_id: currentAssignee.value.id,
    assignee_type: currentAssignee.value.type ?? 'Member',
  });
}

async function loadTeamsIfNeeded() {
  if (!allowTeam.value) return;

  const response = await Api.organization.workflowReviewAssignee.index();
  teamItems.value = mergeCurrentTeamAssignee(response?.data?.teams ?? []);
}

function mergeCurrentTeamAssignee(items) {
  const assignee = currentAssignee.value;
  if (assignee.type !== 'Team' || !assignee.id) return items;
  if (items.some((team) => team.id === assignee.id)) return items;

  return [{ id: assignee.id, name: assignee.name }, ...items];
}

async function searchMembers(query) {
  memberQueryCount.value += 1;
  const currentCount = memberQueryCount.value;
  memberSearchLoading.value = true;

  const response = await Api.organization.member.index({ is_specialist: true, query });
  if (memberQueryCount.value !== currentCount) return;

  memberItems.value = mergeCurrentMemberAssignee(response?.data ?? []);
  memberSearchLoading.value = false;
}

function mergeCurrentMemberAssignee(items) {
  const assignee = currentAssignee.value;
  if (assignee.type !== 'Member' || !assignee.id) return items;
  if (items.some((member) => member.id === assignee.id)) return items;

  return [
    {
      id: assignee.id,
      name: assignee.name,
      email: assignee.email,
    },
    ...items,
  ];
}

async function saveAssignment(formData) {
  const unassigning = !!formData._unassign;
  const assigneeId = unassigning ? null : formData.assignee_id;
  const assigneeType = unassigning ? null : formData.assignee_type;

  if (!unassigning && !assigneeId) return;
  if (!assignActionAvailable.value) return;

  processing.value = true;
  try {
    const response = await persistAssignment(assigneeId, assigneeType);
    if (!response) return;

    emit('save', response.data);
    assignDialog.value?.close();
    eventBus.chime(unassigning ? 'Unassigned' : 'Assigned');
  } finally {
    processing.value = false;
  }
}

async function persistAssignment(assigneeId, assigneeType) {
  if (props.workflowInstance) {
    return scopedApi.workflowInstance.update(props.workflowInstance.id, {
      assignee_id: assigneeId,
      assignee_type: assigneeId ? assigneeType : null,
    });
  }
  return scopedApi.license.update(props.license.id, { reviewer_id: assigneeId });
}
</script>
